import { Injectable } from '@angular/core';
import { from, Observable, Subject } from 'rxjs';
import { CommonDataService } from 'src/sb-tools/typescript/services/common-data.service';
import { AuthenticationService } from 'src/sb-tools/typescript/authentication/authentication.service';
import { environment } from '@environment';
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { FolderCountsUpdatedEventModel } from '../../core/models/dto/signalR/folder-counts-updated-event-model.model';
import { NoteCountUpdatedEventModel } from '../../core/models/dto/signalR/note-count-updated-event-model.model';
import { NotifyAboutNewSurveyResponseAddedEventModel } from '../../core/models/dto/signalR/notify-about-new-survey-response-added-event-model.model';
import { NotificationAddedEventModel } from '../../core/models/dto/signalR/notification-added-event-model.model';
import { NotificationStatusChangedEventModel } from '../../core/models/dto/signalR/notification-status-changed-event-model.model';
import { UnreadNotificationCountUpdatedEventModel } from '../../core/models/dto/signalR/unread-notifications-count-event-model.model';
import { TicketAddedEventModel } from '../../core/models/dto/signalR/ticket-added-event-model.model';
import { TicketAssignedEventModel } from '../../core/models/dto/signalR/ticket-assigned-event-model.model';
import { TicketAssigneeUpdatedEventModel } from '../../core/models/dto/signalR/ticket-assignee-updated-event-model.model';
import { TicketThreadUpdatedEventModel } from '../../core/models/dto/signalR/ticket-thread-updated-event-model.model';
import { TicketUpdatedEventModel } from '../../core/models/dto/signalR/ticket-updated-event-model.model';
import { TicketPropertiesUpdatedEventModel } from '../../core/models/dto/signalR/ticket-properties-updated-event-model.model';
import { TicketAuthorOnlineStatusUpdatedEventModel } from '../../core/models/dto/signalR/ticket-author-online-status-updated-event-model.model';
import { TicketFieldsUpdatedEventModel } from '../../core/models/dto/signalR/ticket-fields-updated-event-model.model';
import { NotifyAboutNewReplyEventModel } from '../../core/models/dto/signalR/notify-about-new-reply-event-model.model';
import { ReplyAddedEventModel } from '../../core/models/dto/signalR/reply-added-event-model.model';
import { ReplyDeletedEventModel } from '../../core/models/dto/signalR/reply-deleted-event-model.model';
import { ReplyPropertiesUpdatedEventModel } from '../../core/models/dto/signalR/reply-properties-updated-event-model.model';
import { ReplySuggestionsUpdatedEventModel } from '../../core/models/dto/signalR/reply-suggestions-updated-event-model.model';
import { TypingStatusUpdatedEventModel } from '../../core/models/dto/signalR/typing-status-updated-event-model.model';
import { ReplyUpdatedEventModel } from '../../core/models/dto/signalR/reply-updated-event-model.model';
import { FilteredRepliesCountUpdatedEventModel } from '../../core/models/dto/signalR/filtered-replies-count-updated-event-model.model';
import { SatisfactionEventModel } from '../../core/models/dto/signalR/satisfaction-event-model.model';
import { TicketSurveyAddedEventModel } from '../../core/models/dto/signalR/ticket-survey-added-event-model.model.ts';
import { NoteEventDTO } from '../models/dto/signalR/note-event.dto';
import { NoteDeletedEventDTO } from '../models/dto/signalR/note-deleted-event.dto';
import { TicketsSearchRequest } from '../../core/models/tickets-search-request.model';
import { NotificationDeletedEventModel } from '../models/dto/signalR/notification-deleted-event-model.model';
import { UserUpdatedEventModel } from '../models/dto/signalR/user-updated-event-model.model';

@Injectable({ providedIn: 'root' })
export class SignalRService {
    connection: HubConnection;

    private subjects = {
        onConnected: new Subject<void>(),

        folderCountersUpdated: new Subject<FolderCountsUpdatedEventModel>(),
        ticketListUpdated: new Subject<void>(),

        noteCountUpdated: new Subject<NoteCountUpdatedEventModel>(),
        notifyAboutSurveyResponseAdded: new Subject<NotifyAboutNewSurveyResponseAddedEventModel>(),
        notificationAdded: new Subject<NotificationAddedEventModel>(),
        notificationStatusUpdated: new Subject<NotificationStatusChangedEventModel>(),
        unreadNotificationCountUpdated: new Subject<UnreadNotificationCountUpdatedEventModel>(),
        notificationDeleted: new Subject<NotificationDeletedEventModel>(),

        ticketAdded: new Subject<TicketAddedEventModel>(),
        ticketAssigned: new Subject<TicketAssignedEventModel>(),
        ticketAssigneeUpdated: new Subject<TicketAssigneeUpdatedEventModel>(),
        ticketThreadUpdated: new Subject<TicketThreadUpdatedEventModel>(),
        ticketUpdated: new Subject<TicketUpdatedEventModel>(),
        userUpdated: new Subject<UserUpdatedEventModel>(),
        ticketPropertiesUpdated: new Subject<TicketPropertiesUpdatedEventModel>(),
        ticketAuthorOnlineStatusUpdated: new Subject<TicketAuthorOnlineStatusUpdatedEventModel>(),
        ticketFieldsUpdated: new Subject<TicketFieldsUpdatedEventModel>(),

        notifyAboutNewReply: new Subject<NotifyAboutNewReplyEventModel>(),
        replyAdded: new Subject<ReplyAddedEventModel>(),
        replyDeleted: new Subject<ReplyDeletedEventModel>(),
        replyDraftDeleted: new Subject<ReplyDeletedEventModel>(),
        replyPropertiesUpdated: new Subject<ReplyPropertiesUpdatedEventModel>(),
        replySuggestionsUpdated: new Subject<ReplySuggestionsUpdatedEventModel>(),
        replyTypingStatusUpdated: new Subject<TypingStatusUpdatedEventModel>(),
        replyUpdated: new Subject<ReplyUpdatedEventModel>(),
        filteredRepliesCountUpdated: new Subject<FilteredRepliesCountUpdatedEventModel>(),

        satisfactionAdded: new Subject<SatisfactionEventModel>(),
        satisfactionUpdated: new Subject<SatisfactionEventModel>(),

        surveyAdded: new Subject<TicketSurveyAddedEventModel>(),

        noteAdded: new Subject<NoteEventDTO>(),
        noteDeleted: new Subject<NoteDeletedEventDTO>(),
        noteUpdated: new Subject<NoteEventDTO>(),
    };

    constructor(
        private commonDataService: CommonDataService,
        private authenticationService: AuthenticationService,
    ) {}

    private getAuthorizationParams(isDisconnected?: boolean): Promise<string> {
        if (!this.commonDataService.isOneTime) {
            return this.authenticationService
                .getAuthorizationHeader(
                    this.authenticationService.community,
                    false,
                    isDisconnected ? 'SignalR disconnected' : 'SignalR reconnect',
                )
                .then(() => {
                    const params =
                        '?community=' +
                        this.authenticationService.community +
                        '&Authorization=' +
                        encodeURIComponent(this.authenticationService.signalRToken);
                    return params;
                });
        } else {
            const params =
                '?community=' +
                this.authenticationService.community +
                '&X-Token=' +
                encodeURIComponent(this.commonDataService.xToken);
            return Promise.resolve(params);
        }
    }

    private connect(authorizationParams: string): void {
        console.log('SignalR connects');

        this.connection = new HubConnectionBuilder()
            .withUrl(
                environment.signalrServerUrl + '/ticketHub' + authorizationParams,
                // used WebSockets or ServerSentEvents as HttpTransportType
            )
            .configureLogging(LogLevel.Information)
            .build();

        this.activateClientsListeners();

        this.connection
            .start()
            .then(() => {
                console.log('SignalR connected');
                console.log(this.connection);
                this.subjects.onConnected.next();
            })
            .catch(() => {
                setTimeout(() => {
                    this.reconnect();
                }, 5000);
            });

        this.connection.onclose(() => {
            console.log('SignalR disconnected');
            setTimeout(() => {
                this.reconnect(true);
            }, 5000);
        });
    }

    reconnect(isDisconnected?: boolean): void {
        if (this.connection) {
            this.connection.stop();
        }
        this.connection = null;

        this.getAuthorizationParams(isDisconnected).then((params) => {
            this.connect(params);
        });
    }

    private activateClientsListeners(): void {
        // #region Global
        this.connection.on('folderCountersUpdated', (counts: FolderCountsUpdatedEventModel) => {
            this.subjects.folderCountersUpdated.next(counts);
        });

        this.connection.on('ticketListUpdated', () => {
            this.subjects.ticketListUpdated.next();
        });

        // #region Notifications
        this.connection.on(
            'notifyAboutSurveyResponseAdded',
            (notification: NotifyAboutNewSurveyResponseAddedEventModel) => {
                this.subjects.notifyAboutSurveyResponseAdded.next(notification);
            },
        );

        this.connection.on('notificationAdded', (notification: NotificationAddedEventModel) => {
            this.subjects.notificationAdded.next(notification);
        });

        this.connection.on('notificationStatusUpdated', (status: NotificationStatusChangedEventModel) => {
            this.subjects.notificationStatusUpdated.next(status);
        });

        this.connection.on('unreadNotificationCountUpdated', (count: UnreadNotificationCountUpdatedEventModel) => {
            this.subjects.unreadNotificationCountUpdated.next(count);
        });

        this.connection.on('notificationDeleted', (notifications: NotificationDeletedEventModel) => {
            this.subjects.notificationDeleted.next(notifications);
        });

        // #region Ticket
        this.connection.on('ticketAdded', (ticket: TicketAddedEventModel) => {
            this.subjects.ticketAdded.next(ticket);
        });

        this.connection.on('ticketAssigned', (assigned: TicketAssignedEventModel) => {
            this.subjects.ticketAssigned.next(assigned);
        });

        this.connection.on('ticketAssigneeUpdated', (assignee: TicketAssigneeUpdatedEventModel) => {
            this.subjects.ticketAssigneeUpdated.next(assignee);
        });

        this.connection.on('ticketThreadUpdated', (thread: TicketThreadUpdatedEventModel) => {
            this.subjects.ticketThreadUpdated.next(thread);
        });

        this.connection.on('ticketUpdated', (ticket: TicketUpdatedEventModel) => {
            this.subjects.ticketUpdated.next(ticket);
        });

        this.connection.on('userUpdated', (user: UserUpdatedEventModel) => {
            this.subjects.userUpdated.next(user);
        });

        this.connection.on('ticketPropertiesUpdated', (ticket: TicketPropertiesUpdatedEventModel) => {
            this.subjects.ticketPropertiesUpdated.next(ticket);
        });

        this.connection.on('ticketAuthorOnlineStatusUpdated', (status: TicketAuthorOnlineStatusUpdatedEventModel) => {
            this.subjects.ticketAuthorOnlineStatusUpdated.next(status);
        });

        this.connection.on('ticketFieldsUpdated', (fields: TicketFieldsUpdatedEventModel) => {
            this.subjects.ticketFieldsUpdated.next(fields);
        });

        this.connection.on('noteCountUpdated', (eventModel: NoteCountUpdatedEventModel) => {
            this.subjects.noteCountUpdated.next(eventModel);
        });

        // #region Replies
        this.connection.on('notifyAboutNewReply', (notify: NotifyAboutNewReplyEventModel) => {
            this.subjects.notifyAboutNewReply.next(notify);
        });

        this.connection.on('replyAdded', (reply: ReplyAddedEventModel) => {
            this.subjects.replyAdded.next(reply);
        });

        this.connection.on('replyDeleted', (eventModel: ReplyDeletedEventModel) => {
            this.subjects.replyDeleted.next(eventModel);
        });

        this.connection.on('replyDraftDeleted', (eventModel: ReplyDeletedEventModel) => {
            this.subjects.replyDraftDeleted.next(eventModel);
        });

        this.connection.on('replyPropertiesUpdated', (replyUpdated: ReplyPropertiesUpdatedEventModel) => {
            this.subjects.replyPropertiesUpdated.next(replyUpdated);
        });

        this.connection.on('replySuggestionsUpdated', (suggestion: ReplySuggestionsUpdatedEventModel) => {
            this.subjects.replySuggestionsUpdated.next(suggestion);
        });

        this.connection.on('replyTypingStatusUpdated', (status: TypingStatusUpdatedEventModel) => {
            this.subjects.replyTypingStatusUpdated.next(status);
        });

        this.connection.on('replyUpdated', (reply: ReplyUpdatedEventModel) => {
            this.subjects.replyUpdated.next(reply);
        });

        this.connection.on('filteredRepliesCountUpdated', (count: FilteredRepliesCountUpdatedEventModel) => {
            this.subjects.filteredRepliesCountUpdated.next(count);
        });

        //  #region satisfaction
        this.connection.on('satisfactionAdded', (satisfaction: SatisfactionEventModel) => {
            this.subjects.satisfactionAdded.next(satisfaction);
        });

        this.connection.on('satisfactionUpdated', (satisfaction: SatisfactionEventModel) => {
            this.subjects.satisfactionUpdated.next(satisfaction);
        });

        this.connection.on('surveyAdded', (survey: TicketSurveyAddedEventModel) => {
            this.subjects.surveyAdded.next(survey);
        });

        //  #region Comments
        this.connection.on('noteAdded', (comment: NoteEventDTO) => {
            this.subjects.noteAdded.next(comment);
        });

        this.connection.on('noteDeleted', (comment: NoteDeletedEventDTO) => {
            this.subjects.noteDeleted.next(comment);
        });

        this.connection.on('noteUpdated', (comment: NoteEventDTO) => {
            this.subjects.noteUpdated.next(comment);
        });
    }

    /// /////////////////// LOCAL METHODS ////////////////////

    onConnected(): Observable<void> {
        return this.subjects.onConnected.asObservable();
    }

    /// /////////////////// SERVER METHODS ////////////////////

    searchRequestChanged(searchRequest: TicketsSearchRequest): Observable<void> {
        if (this.connection?.state === HubConnectionState.Connected) {
            return from(this.connection.invoke('searchRequestChanged', searchRequest));
        } else {
            return new Observable();
        }
    }

    selectedTicketChanged(ticketId?: number): Observable<void> {
        if (this.connection?.state === HubConnectionState.Connected) {
            return from(this.connection.invoke('selectedTicketChanged', ticketId));
        } else {
            return new Observable();
        }
    }

    windowFocusStateChanged(hasFocus: boolean): Observable<void> {
        if (this.connection?.state === HubConnectionState.Connected) {
            return from(this.connection.invoke('windowFocusStateChanged', hasFocus));
        } else {
            return new Observable();
        }
    }

    updateReplyTypingStatus(typingStatus: TypingStatusUpdatedEventModel): Observable<void> {
        if (this.connection?.state === HubConnectionState.Connected) {
            return from(this.connection.invoke('updateReplyTypingStatus', typingStatus));
        } else {
            return new Observable();
        }
    }

    isTicketAuthorOnline(ticketId: number): Observable<boolean> {
        if (this.connection?.state === HubConnectionState.Connected) {
            return from(this.connection.invoke('isTicketAuthorOnline', ticketId));
        } else {
            return new Observable(null);
        }
    }

    /// /////////////////// CLIENT METHODS ////////////////////

    onFolderCountersUpdated(): Observable<FolderCountsUpdatedEventModel> {
        return this.subjects.folderCountersUpdated.asObservable();
    }

    onTicketListUpdated(): Observable<void> {
        return this.subjects.ticketListUpdated.asObservable();
    }

    onNoteCountUpdated(): Observable<NoteCountUpdatedEventModel> {
        return this.subjects.noteCountUpdated.asObservable();
    }

    onNotifyAboutSurveyResponseAdded(): Observable<NotifyAboutNewSurveyResponseAddedEventModel> {
        return this.subjects.notifyAboutSurveyResponseAdded.asObservable();
    }

    onNotificationAdded(): Observable<NotificationAddedEventModel> {
        return this.subjects.notificationAdded.asObservable();
    }

    onNotificationStatusUpdated(): Observable<NotificationStatusChangedEventModel> {
        return this.subjects.notificationStatusUpdated.asObservable();
    }

    onUnreadNotificationCountUpdated(): Observable<UnreadNotificationCountUpdatedEventModel> {
        return this.subjects.unreadNotificationCountUpdated.asObservable();
    }

    onNotificationDeleted(): Observable<NotificationDeletedEventModel> {
        return this.subjects.notificationDeleted.asObservable();
    }

    onTicketAdded(): Observable<TicketAddedEventModel> {
        return this.subjects.ticketAdded.asObservable();
    }

    onTicketAssigned(): Observable<TicketAssignedEventModel> {
        return this.subjects.ticketAssigned.asObservable();
    }

    onTicketAssigneeUpdated(): Observable<TicketAssigneeUpdatedEventModel> {
        return this.subjects.ticketAssigneeUpdated.asObservable();
    }

    onTicketThreadUpdated(): Observable<TicketThreadUpdatedEventModel> {
        return this.subjects.ticketThreadUpdated.asObservable();
    }

    onTicketUpdated(): Observable<TicketUpdatedEventModel> {
        return this.subjects.ticketUpdated.asObservable();
    }

    onUserUpdated(): Observable<UserUpdatedEventModel> {
        return this.subjects.userUpdated.asObservable();
    }

    onTicketPropertiesUpdated(): Observable<TicketPropertiesUpdatedEventModel> {
        return this.subjects.ticketPropertiesUpdated.asObservable();
    }

    onTicketAuthorOnlineStatusUpdated(): Observable<TicketAuthorOnlineStatusUpdatedEventModel> {
        return this.subjects.ticketAuthorOnlineStatusUpdated.asObservable();
    }

    onTicketFieldsUpdated(): Observable<TicketFieldsUpdatedEventModel> {
        return this.subjects.ticketFieldsUpdated.asObservable();
    }

    onNotifyAboutNewReply(): Observable<NotifyAboutNewReplyEventModel> {
        return this.subjects.notifyAboutNewReply.asObservable();
    }

    onReplyAdded(): Observable<ReplyAddedEventModel> {
        return this.subjects.replyAdded.asObservable();
    }

    onReplyDeleted(): Observable<ReplyDeletedEventModel> {
        return this.subjects.replyDeleted.asObservable();
    }

    onReplyDraftDeleted(): Observable<ReplyDeletedEventModel> {
        return this.subjects.replyDraftDeleted.asObservable();
    }

    onReplyPropertiesUpdated(): Observable<ReplyPropertiesUpdatedEventModel> {
        return this.subjects.replyPropertiesUpdated.asObservable();
    }

    onReplySuggestionsUpdated(): Observable<ReplySuggestionsUpdatedEventModel> {
        return this.subjects.replySuggestionsUpdated.asObservable();
    }

    onReplyTypingStatusUpdated(): Observable<TypingStatusUpdatedEventModel> {
        return this.subjects.replyTypingStatusUpdated.asObservable();
    }

    onReplyUpdated(): Observable<ReplyUpdatedEventModel> {
        return this.subjects.replyUpdated.asObservable();
    }

    onFilteredRepliesCountUpdated(): Observable<FilteredRepliesCountUpdatedEventModel> {
        return this.subjects.filteredRepliesCountUpdated.asObservable();
    }

    onSatisfactionAdded(): Observable<SatisfactionEventModel> {
        return this.subjects.satisfactionAdded.asObservable();
    }

    onSatisfactionUpdated(): Observable<SatisfactionEventModel> {
        return this.subjects.satisfactionUpdated.asObservable();
    }

    onSurveyAdded(): Observable<TicketSurveyAddedEventModel> {
        return this.subjects.surveyAdded.asObservable();
    }

    onNoteAdded(): Observable<NoteEventDTO> {
        return this.subjects.noteAdded.asObservable();
    }

    onNoteDeleted(): Observable<NoteDeletedEventDTO> {
        return this.subjects.noteDeleted.asObservable();
    }

    onNoteUpdated(): Observable<NoteEventDTO> {
        return this.subjects.noteUpdated.asObservable();
    }
}
